import { useRouter } from 'next/router';
import React, { FC, useState } from 'react';

import { Alert, Anchor, Button, Stack, Text } from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';

import { isLoginErrorString, UserReferrer, UserType } from 'models';
import { UserHelpers } from 'helpers/UserHelpers';

import { Form } from 'components/forms/Form';
import { FormStatus } from 'components/forms/FormStatus';
import { FormSubmit } from 'components/forms/FormSubmit';
import { TextField } from 'components/forms/fields/TextField';
import { LICENSE_AGREEMENT_KEY } from 'components/license-agreement-modal/LicenseAgreementModal';
import { useLocalUserInfo } from 'hooks/use-local-user-info';
import { AuthenticationService } from 'services/AuthenticationService';
import { InternalTrackingService } from 'services/InternalTrackingService';
import { LocalStorageService } from 'services/LocalStorageService';

type LoginFormFieldValues = {
    email: string;
    referrer?: UserReferrer;
    type?: UserType;
};

const emailAddressRegex = /[A-Z0-9a-z._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,64}/;

type LoginFormProps = {
    hideEnterCode?: boolean;
    redirect?: string;
    afterSubmit?: (email: string, userId: string) => Promise<void>;
    afterLogin?: () => void;
    startWithCode?: boolean;
};

enum LoginFormScreen {
    login,
    enterCode,
}

const LoginForm: FC<LoginFormProps> = ({ redirect, afterSubmit, afterLogin, hideEnterCode, startWithCode }) => {
    const [email, setEmail] = useLocalStorage<string | undefined>({
        key: UserHelpers.localStorageKey.email,
    });

    const [screen, setScreen] = useState<LoginFormScreen>(
        startWithCode ? LoginFormScreen.enterCode : LoginFormScreen.login,
    );

    return (
        <>
            {screen === LoginFormScreen.enterCode && email ? (
                <EnterCodeScreen email={email} setScreen={setScreen} afterLogin={afterLogin} />
            ) : (
                <LoginScreen
                    email={email}
                    redirect={redirect}
                    setScreen={setScreen}
                    hideEnterCode={hideEnterCode}
                    afterSubmit={async (email, userId) => {
                        afterSubmit?.(email, userId).then();

                        setEmail(email);
                        setScreen(LoginFormScreen.enterCode);
                    }}
                />
            )}
        </>
    );
};

const LoginScreen: FC<
    LoginFormProps & {
        email?: string;
        setScreen: (screen: LoginFormScreen) => void;
    }
> = ({ redirect, afterSubmit, email = '', setScreen, hideEnterCode }) => {
    const { query } = useRouter();
    const { referrer, type } = useLocalUserInfo();

    return (
        <Form<LoginFormFieldValues>
            defaultValues={{
                email: LocalStorageService.get(UserHelpers.localStorageKey.email) ?? email,
                referrer,
                type,
            }}
            onSubmit={async ({ email, ...values }, { setError, setSubmitError }) => {
                if (!emailAddressRegex.test(email)) {
                    setError('email', { message: 'Invalid email address', type: 'pattern' });
                    return;
                }

                const autojoin =
                    query.team && query.teamCode
                        ? {
                              team: query.team as string,
                              code: query.teamCode as string,
                          }
                        : undefined;

                try {
                    const response = await AuthenticationService.sendMagicTokenLogin(email, values, redirect, autojoin);

                    LocalStorageService.store(LICENSE_AGREEMENT_KEY, true);

                    InternalTrackingService.track('login.request', {
                        email,
                    });

                    afterSubmit?.(email, response?.userId);
                } catch (error) {
                    setSubmitError(error instanceof Error ? error.message : 'Error sending login link');
                }
            }}
        >
            <Stack gap="sm">
                {query.reason === 'expired' && (
                    <Alert color="blue" p="xs">
                        Your session has expired. Please log in again.
                    </Alert>
                )}
                <TextField
                    size="md"
                    data-autofocus
                    name="email"
                    type="email"
                    autoComplete="email"
                    required={true}
                    label="Email"
                    data-cy="login-email"
                    description={
                        referrer === UserReferrer.REPLUS &&
                        'To speed up verification of your account, consider signing up using your professional email address.'
                    }
                />
                <Text size="xs" c="dimmed">
                    By continuing, you acknowledge that you have read, understood, and agree to the{' '}
                    <Anchor target="_blank" href="/license-agreement" c="inherit">
                        License Agreement
                    </Anchor>
                    {', '}
                    <Anchor target="_blank" href="/dcide-privacy-policy.pdf" c="inherit">
                        Privacy Policy
                    </Anchor>
                    {' & '}
                    <Anchor target="_blank" href="/dcide-data-processing.pdf" c="inherit">
                        Data Processing
                    </Anchor>
                    .
                </Text>

                <FormSubmit variant="filled" size="md">
                    Accept and continue
                </FormSubmit>

                <FormStatus />

                {email && !hideEnterCode && (
                    <Button variant="transparent" size="md" onClick={() => setScreen(LoginFormScreen.enterCode)}>
                        Enter login code
                    </Button>
                )}
            </Stack>
        </Form>
    );
};

type EnterCodeValues = {
    code: string;
};

const EnterCodeScreen: FC<{
    email: string;
    setScreen: (screen: LoginFormScreen) => void;
    afterLogin?: () => void;
}> = ({ email, setScreen, afterLogin }) => {
    const router = useRouter();
    const [error, setError] = useState('');

    const submit = async (values: EnterCodeValues) => {
        try {
            const result = await AuthenticationService.getMagicLoginToken(email, values.code);

            if (result.token) {
                InternalTrackingService.track('login.success', { email });

                router.replace(`/token/${result.token}`).then();
                afterLogin?.();
            } else {
                console.error('getMagicLoginToken returned no token and did not throw an error');
            }
        } catch (error) {
            const message =
                typeof error === 'object' && error !== null && 'message' in error
                    ? String(error.message)
                    : 'Error using login code';

            setError(message);

            if (!isLoginErrorString(message)) {
                console.error('Unexpected login code error:', message, error);
            }
        }
    };

    return (
        <Form<EnterCodeValues> defaultValues={{ code: '' }} onSubmit={submit}>
            <Stack>
                {email && (
                    <>
                        <Stack gap={0}>
                            <Text>
                                A login link has been sent to <strong>{email}</strong>
                            </Text>

                            <Text mt={8}>
                                Please check your inbox for the login code. If you didn’t receive a code, click{' '}
                                <strong>Return to login</strong> to resend it.
                            </Text>
                        </Stack>
                    </>
                )}
                <Stack gap="xs" w="100%">
                    <TextField
                        name="code"
                        label="Enter your login code"
                        autoComplete="one-time-code"
                        // iOS only offers codes from Mail/Messages on text inputs, not type="number"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        required
                        size="md"
                    />
                    <FormSubmit size="md">Login with code</FormSubmit>
                    {error && (
                        <Alert p="xs" color="red">
                            {error}
                        </Alert>
                    )}
                </Stack>
                <Button variant="transparent" size="md" onClick={() => setScreen(LoginFormScreen.login)}>
                    Return to login
                </Button>
            </Stack>
        </Form>
    );
};

export { LoginForm };
